import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader } from '../ui/Card';
import { CardListToolbar } from '../ui/CardListToolbar';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { FilterTabs } from '../ui/FilterTabs';
import { AlertMessage } from '../ui/AlertMessage';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRow } from '../ui/Skeleton';
import { ModuleAccessBanner } from '../ui/ModuleAccessBanner';
import { usePermissions } from '../../permissions/PermissionContext';
import { api, extractErrorMessage } from '../../api/client';
import {
  CHALLAN_PARTY_TYPES,
  CHALLAN_TYPE_LABEL,
  challanEditPath,
  challansNewPath,
  downloadChallanPdf,
  formatDate,
} from './challanUtils';
import ChallanPreviewModal from './ChallanPreviewModal';

const SEARCH_DEBOUNCE_MS = 300;

/** One colour per party type, so the list can be scanned quickly. */
const TYPE_TONE = {
  BUTTA_CUTTING_PARTIES: 'primary',
  MILL_FINISH_PARTIES: 'info',
  BORDER_CUTTING_PARTIES: 'warning',
  HANDWORK_PARTIES: 'secondary',
};

/** Same action button look as the Designs list. */
const ICON_BTN =
  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200/80 bg-white shadow-sm transition-colors';

/** Party address as printed on the challan ("Delivered at"). */
function deliveredAt(p) {
  const cityState = [p.city, p.state].map((x) => String(x || '').trim()).filter(Boolean).join(', ');
  return [p.addressLine1, p.addressLine2, cityState].map((x) => String(x || '').trim()).filter(Boolean).join(', ');
}

/**
 * Challan list: one row per party copy (a challan sent to Butta + Border shows two rows with the
 * same number), filtered by party type. Newest first.
 */
export default function ChallanList() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canCreate = can('CHALLANS', 'CREATE');
  const canEdit = can('CHALLANS', 'EDIT');

  const [items, setItems] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [partyType, setPartyType] = useState('ALL');
  const [pageError, setPageError] = useState('');
  const [downloadingKey, setDownloadingKey] = useState('');
  /** Row whose formatted challan (PDF) is shown in the preview. */
  const [preview, setPreview] = useState(null);
  const requestSeq = useRef(0);

  const fetchPage = useCallback(async (searchText, type, before) => {
    const params = new URLSearchParams();
    if (searchText.trim()) params.set('search', searchText.trim());
    if (type !== 'ALL') params.set('category', type);
    if (before) params.set('before', before);
    const qs = params.toString();
    const { data } = await api.get(`/api/challans${qs ? `?${qs}` : ''}`);
    return data || {};
  }, []);

  useEffect(() => {
    const seq = ++requestSeq.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      setPageError('');
      try {
        const data = await fetchPage(search, partyType, null);
        if (seq !== requestSeq.current) return;
        setItems(Array.isArray(data.items) ? data.items : []);
        setNextBefore(data.nextBefore || null);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setItems([]);
        setNextBefore(null);
        setPageError(extractErrorMessage(err, 'Unable to load challans'));
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, search ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [search, partyType, fetchPage]);

  const loadMore = async () => {
    if (!nextBefore || loadingMore) return;
    const seq = requestSeq.current;
    setLoadingMore(true);
    try {
      const data = await fetchPage(search, partyType, nextBefore);
      if (seq !== requestSeq.current) return;
      const more = Array.isArray(data.items) ? data.items : [];
      setItems((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        return [...prev, ...more.filter((c) => !seen.has(c.id))];
      });
      setNextBefore(data.nextBefore || null);
    } catch (err) {
      setPageError(extractErrorMessage(err, 'Unable to load more challans'));
    } finally {
      setLoadingMore(false);
    }
  };

  /** One row per party copy, limited to the selected party type. */
  const rows = useMemo(() => {
    const out = [];
    items.forEach((c) => {
      (c.parties || []).forEach((p) => {
        if (partyType !== 'ALL' && p.category !== partyType) return;
        out.push({ key: `${c.id}-${p.category}`, challan: c, party: p });
      });
    });
    return out;
  }, [items, partyType]);

  const handleDownload = async (row) => {
    setDownloadingKey(row.key);
    setPageError('');
    try {
      await downloadChallanPdf(row.challan, row.party.category);
    } catch (err) {
      setPageError(err?.message || 'Unable to download PDF');
    } finally {
      setDownloadingKey('');
    }
  };

  const tabs = [
    { id: 'ALL', label: 'All' },
    ...CHALLAN_PARTY_TYPES.map((t) => ({ id: t.key, label: t.label })),
  ];

  return (
    <div className="mx-auto w-full max-w-[1500px] space-y-3">
      <ModuleAccessBanner moduleKey="CHALLANS" />
      <Card>
        <CardHeader variant="gradient">
          <CardListToolbar
            tone="light"
            title="Delivery Challans"
            actions={(
              <>
                <div className="w-full sm:w-72">
                  <Input
                    type="text"
                    placeholder="Search challan no. or party"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    leftIcon={(
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                  />
                </div>
                {canCreate ? (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => navigate(challansNewPath())}
                    leftIcon={(
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    )}
                  >
                    New Challan
                  </Button>
                ) : null}
              </>
            )}
          />
        </CardHeader>

        <CardBody>
          <AlertMessage message={pageError} type="error" onClose={() => setPageError('')} className="mb-4" />

          <div className="mb-4">
            <FilterTabs value={partyType} onChange={setPartyType} tabs={tabs} />
          </div>

          {!loading && rows.length === 0 ? (
            <EmptyState
              title={search || partyType !== 'ALL' ? 'No challans found' : 'No challans yet'}
              description={search || partyType !== 'ALL' ? 'Try another party type or search.' : 'Create the first delivery challan.'}
              action={!search && partyType === 'ALL' && canCreate ? (
                <Button type="button" variant="primary" onClick={() => navigate(challansNewPath())}>
                  New Challan
                </Button>
              ) : null}
            />
          ) : (
            <>
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="table-modern">
                  <thead>
                    <tr>
                      <th className="w-28">Challan No.</th>
                      <th className="w-32">Date</th>
                      <th className="w-40">Party type</th>
                      <th>Delivered at</th>
                      <th className="text-right pr-6">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading
                      ? Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} columns={5} />)
                      : rows.map((row) => {
                          const { challan: c, party: p } = row;
                          const address = deliveredAt(p);
                          return (
                            <tr
                              key={row.key}
                              className="cursor-pointer"
                              onClick={() => setPreview(row)}
                              title="Click to preview the challan"
                            >
                              <td>
                                <span className="font-bold tabular-nums text-slate-900">{c.challanNo}</span>
                                <span className="block text-[11px] text-slate-400">FY {c.financialYear}</span>
                              </td>
                              <td className="tabular-nums text-slate-700">{formatDate(c.challanDate)}</td>
                              <td>
                                <Badge tone={TYPE_TONE[p.category] || 'neutral'}>{CHALLAN_TYPE_LABEL[p.category] || p.category}</Badge>
                              </td>
                              <td className="max-w-[460px]">
                                <span className="block truncate font-medium text-slate-800" title={p.partyName}>{p.partyName}</span>
                                {address ? (
                                  <span className="block truncate text-xs text-slate-500" title={address}>{address}</span>
                                ) : null}
                              </td>
                              <td className="pr-6">
                                {/* stopPropagation: the buttons do their own action, not the row-click preview */}
                                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    onClick={() => setPreview(row)}
                                    className={`${ICON_BTN} text-amber-500 hover:bg-amber-50 hover:border-amber-200`}
                                    title="Preview"
                                    aria-label={`Preview challan ${c.challanNo} for ${p.partyName}`}
                                  >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24" aria-hidden>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                  </button>
                                  {canEdit ? (
                                    <button
                                      type="button"
                                      onClick={() => navigate(challanEditPath(c.id))}
                                      className={`${ICON_BTN} text-sky-600 hover:bg-sky-50 hover:border-sky-200`}
                                      title="Edit"
                                      aria-label={`Edit challan ${c.challanNo}`}
                                    >
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    onClick={() => handleDownload(row)}
                                    disabled={downloadingKey === row.key}
                                    className={`${ICON_BTN} text-red-600 hover:bg-red-50 hover:border-red-200 disabled:cursor-wait disabled:opacity-60`}
                                    title="Download PDF"
                                    aria-label={`Download PDF of challan ${c.challanNo} for ${p.partyName}`}
                                  >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24" aria-hidden>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7.5L14.5 2z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v4a2 2 0 002 2h4" />
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-6" />
                                      <path strokeLinecap="round" strokeLinejoin="round" d="m9 15 3 3 3-3" />
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                  </tbody>
                </table>
              </div>
              {nextBefore && !loading ? (
                <div className="mt-4 flex justify-center">
                  <Button type="button" variant="secondary" onClick={loadMore} loading={loadingMore} disabled={loadingMore}>
                    Load more challans
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>

      <ChallanPreviewModal
        open={Boolean(preview)}
        challan={preview ? preview.challan : null}
        copy={preview ? preview.party.category : undefined}
        onClose={() => setPreview(null)}
      />
    </div>
  );
}
